import { AlertTriangle, CheckCircle2, ServerCrash } from "lucide-react";
import { Metric, Section, usePoll, fmtTime, fmtDur, level, TONE } from "@/components/monitor/shared";
import { HourBars } from "@/components/monitor/Charts";
import { IncidentRows, ErrorRows } from "@/components/monitor/Errors";

export function StatusBanner({ data }) {
  const live = data.live;
  const bad = !live.db_ok || data.open_incidents > 0;
  const Icon = !live.db_ok ? ServerCrash : bad ? AlertTriangle : CheckCircle2;
  return (
    <div data-testid="monitor-status-banner" className={`rounded-2xl border px-5 py-4 flex flex-wrap items-center gap-3 ${!live.db_ok ? TONE.bad : bad ? TONE.warn : TONE.ok}`}>
      <Icon className="w-5 h-5 shrink-0" />
      <div className="text-sm">
        <span className="font-semibold">{!live.db_ok ? "Veritabanı bağlantısı yok" : bad ? `${data.open_incidents} açık uyarı var` : "Sunucu çalışıyor, açık uyarı yok"}</span>
        <span className="opacity-80"> · Çalışma süresi {fmtDur(live.uptime_s)} · Ölçüm toplayıcı {data.sampler_running ? "aktif" : "kayıt almıyor"} · {data.samples} dakikalık kayıt</span>
      </div>
    </div>
  );
}

export default function OverviewTab() {
  const { data, error } = usePoll("/admin/monitor/overview", 10000);
  if (error) return <div data-testid="overview-error" className={`rounded-xl border p-4 text-sm ${TONE.bad}`}>Sunucuya bağlanılamadı: {String(error)}</div>;
  if (!data) return <div className="text-sm text-slate-500">Yükleniyor...</div>;
  const l = data.live, s = data.last_sample || {};
  return (
    <div className="space-y-5" data-testid="monitor-overview">
      <StatusBanner data={data} />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric testId="ov-cpu" label={`CPU (${s.cpu_scope === "host" ? "sunucu" : "konteyner"}, son dakika)`} value={s.cpu_pct != null ? `%${s.cpu_pct}` : null} tone={level(s.cpu_pct, 70, 90)} hint={l.cpu_cores ? `${l.cpu_cores} çekirdek sınırı` : null} unsupported="İlk ölçüm bekleniyor" />
        <Metric testId="ov-ram" label="RAM" value={`%${l.ram_pct}`} tone={level(l.ram_pct, 75, 90)} hint={l.ram_scope === "container" ? "Konteyner bellek sınırına göre" : "Sunucu belleği"} />
        <Metric testId="ov-disk" label="Disk doluluk (en yüksek)" value={l.disk_pct != null ? `%${l.disk_pct}` : null} tone={level(l.disk_pct, 80, 90)} />
        <Metric testId="ov-api" label="Ort. API yanıt (son dakika)" value={s.api_avg_ms != null ? `${s.api_avg_ms} ms` : null} hint={s.api_p95_ms != null ? `p95 ${s.api_p95_ms} ms · ${s.req_count} istek` : null} tone={level(s.api_p95_ms, 800, 1500)} unsupported="Son dakikada istek yok" />
        <Metric testId="ov-active" label="Aktif kullanıcı (5 dk)" value={l.active_users} tone="ok" hint="Oturum açmış, son 5 dakikada istek yapan" />
        <Metric testId="ov-concurrent" label="Eş zamanlı kullanıcı (1 dk)" value={l.concurrent_users} tone="ok" hint={`Şu an işlenen istek: ${l.in_flight}`} />
        <Metric testId="ov-db" label="Veritabanı" value={l.db_ok ? `${l.db_ping_ms} ms` : null} tone={l.db_ok ? "ok" : "bad"} unsupported="Bağlantı yok" hint={l.db_conn != null ? `${l.db_conn} bağlantı` : null} />
        <Metric testId="ov-incidents" label="Açık uyarılar" value={data.open_incidents} tone={data.open_incidents ? "warn" : "ok"} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Section title="Son performans uyarıları" testId="ov-recent-incidents"><IncidentRows items={data.incidents} compact /></Section>
        <Section title="Son sistem hataları" testId="ov-recent-errors"><ErrorRows items={data.errors} compact /></Section>
      </div>
      <Section title={`En yoğun kullanım saatleri (son ${data.peaks.days} gün, TR saati)`} testId="ov-peaks">
        <HourBars rows={data.peaks.by_hour} testId="ov-peaks-chart" />
        {data.peaks.busiest.length > 0 && (
          <div className="flex flex-wrap gap-4 text-xs text-slate-600 mt-3">
            <span>En yoğun: {data.peaks.busiest.map((b) => `${String(b.hour).padStart(2, "0")}:00`).join(", ")}</span>
            {data.peaks.quietest.length > 0 && <span>En sakin: {data.peaks.quietest.map((b) => `${String(b.hour).padStart(2, "0")}:00`).join(", ")}</span>}
          </div>
        )}
      </Section>
      <div className="text-[11px] text-slate-400">Son güncelleme: {fmtTime(l.ts)} · 10 saniyede bir yenilenir</div>
    </div>
  );
}
